import type { Product } from "@/mocks/products";

export const NEWEST_COUNT = 20;
export const PROMO_COUNT = 25;

export type CollectionId = "novidades" | "promocoes" | "vendidos";

export const collectionLabels: Record<CollectionId, string> = {
  novidades: "Novidades",
  promocoes: "Promoções",
  vendidos: "Mais vendidos",
};

export function parseCollection(value: string | null): CollectionId | null {
  return value === "novidades" || value === "promocoes" || value === "vendidos" ? value : null;
}

// Dia no fuso da loja (YYYY-MM-DD): a seleção do dia muda à meia-noite de
// Brasília para todos os visitantes, não à meia-noite de cada navegador.
export function storeDay(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo" }).format(now);
}

// As mais novas: os ids da Yampi crescem a cada produto cadastrado, então
// o maior id é o produto mais recente.
export function newestProducts(products: Product[], count = NEWEST_COUNT): Product[] {
  return [...products].sort((a, b) => b.id - a.id).slice(0, count);
}

function hashSeed(text: string): number {
  let h = 1779033703 ^ text.length;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (h >>> 0) || 1;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Sorteio do dia: parte de uma ordem fixa (por id) e embaralha com uma semente
// derivada da data, então todo visitante vê os mesmos produtos no mesmo dia, e
// no dia seguinte a seleção muda.
export function dailyPicks(products: Product[], count = PROMO_COUNT, day: string = storeDay(), salt = "promocoes"): Product[] {
  const pool = [...products].sort((a, b) => a.id - b.id);
  const random = mulberry32(hashSeed(`rica-tanto:${salt}:${day}`));
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

// Ordem da vitrine: embaralhada com a mesma regra do sorteio diário (todo
// visitante vê a mesma ordem no dia; amanhã ela muda).
export function homeOrder(products: Product[], day: string = storeDay()): Product[] {
  return dailyPicks(products, products.length, day, "vitrine");
}

// Texto sem acento e em minúsculas, pra busca ("pérola" acha "perola" e vice-versa).
export function normalizeText(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

// Desconto real (%) do produto, a partir do preço "de" e do preço "por" da Yampi; 0 se não tem.
export function discountPercent(product: Product): number {
  if (!product.compareAtPrice || product.compareAtPrice <= product.price || product.price <= 0) return 0;
  return Math.round((1 - product.price / product.compareAtPrice) * 100);
}
